// src/pages/AboutUs.js
import React from 'react';
import { Link } from 'react-router-dom';

export default function AboutUs() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-14 md:py-20">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900">
                Debate Academy
              </h1>
              <p className="mt-4 text-lg text-gray-700 leading-relaxed">
                We help students, teachers, and clubs build elite debate skills with guided
                practice, instant AI feedback, and classroom workflows — all in one place.
              </p>
              <div className="mt-6 flex gap-3">
                <Link
                  to="/training"
                  className="inline-block bg-blue-600 hover:bg-blue-500 text-white px-5 py-3 rounded-lg font-medium"
                >
                  Start Training
                </Link>
                <Link
                  to="/debater"
                  className="inline-block bg-purple-600 hover:bg-purple-500 text-white px-5 py-3 rounded-lg font-medium"
                >
                  Try AI Debater
                </Link>
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 border rounded-xl p-6">
              <ul className="space-y-3 text-gray-800">
                <li className="flex gap-3">
                  <span className="text-blue-600">✔</span>
                  <span>Structured drills for arguments, refutation, clashes, modeling & framing</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600">✔</span>
                  <span>Instant, rubric-based AI feedback with 10-point scoring</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600">✔</span>
                  <span>Live AI debate that remembers context via threaded conversations</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600">✔</span>
                  <span>Teacher tools: classrooms, lesson assignment, grading & progress</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600">✔</span>
                  <span>Lesson & module builder with quizzes, writing prompts, and AI feedback</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="max-w-6xl mx-auto px-6 py-12 md:py-16">
        <div className="grid md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            <h2 className="text-3xl font-bold text-gray-900">Our Mission</h2>
            <p className="mt-4 text-gray-700 leading-relaxed">
              Make debate training accessible, rigorous, and fun. Debate strengthens critical
              thinking, clarity of speech, and strategic reasoning. We combine expert pedagogy with
              practical, feedback-rich exercises so learners level up faster — from fundamentals to
              advanced round strategy.
            </p>
          </div>
          <div className="bg-white border rounded-xl p-5">
            <h3 className="font-semibold text-gray-900">What We Value</h3>
            <ul className="mt-3 space-y-2 text-gray-700">
              <li><strong>Clarity:</strong> Skills broken into targeted drills</li>
              <li><strong>Iteration:</strong> Fast practice → feedback → improvement</li>
              <li><strong>Access:</strong> Tools for both solo learners and classrooms</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Feature Grid */}
      <section className="bg-white border-y">
        <div className="max-w-6xl mx-auto px-6 py-12 md:py-16">
          <h2 className="text-3xl font-bold text-gray-900">Everything you need to train debate</h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {/* Training Suite */}
            <FeatureCard
              title="Training Suite"
              blurb="Focused drills with instant, rubric-based feedback."
              points={[
                'Arguments (PAEL): Point, Analysis, Example, Link',
                'Refutation: prioritize analysis & impact',
                'Clashes: identify core conflicts & affected actors',
                'Modeling: definitions, mechanisms, examples',
                'Framing: set, diagnose, and reframe the narrative',
              ]}
              cta={{ to: '/training', label: 'Explore Training' }}
            />

            {/* AI Debater */}
            <FeatureCard
              title="AI Debater"
              blurb="Debate the AI. It remembers context and pushes you to clash, weigh, and justify."
              points={[
                'Threaded debates that keep motion + side',
                'Structured or free-form rounds',
                'Real-time counter-argumenting',
              ]}
              cta={{ to: '/debater', label: 'Start a Debate' }}
            />

            {/* Lessons & Modules */}
            <FeatureCard
              title="Lessons & Modules"
              blurb="Create or use lessons with readings, quizzes, and writing prompts."
              points={[
                'Author lessons or generate from URLs',
                'Auto-graded quizzes & AI feedback for writing',
                'Group lessons into modules by level',
              ]}
              cta={{ to: '/modules', label: 'Browse Modules' }}
            />

            {/* Classrooms */}
            <FeatureCard
              title="Classrooms (Teachers)"
              blurb="Assign lessons, track completion, and give feedback — all in one place."
              points={[
                'Create classes & share join codes',
                'Assign lessons from your library',
                'View progress and grade writing',
              ]}
              cta={{ to: '/classrooms', label: 'Open Classrooms' }}
            />

            {/* Progress & Feedback */}
            <FeatureCard
              title="Progress & Feedback"
              blurb="Clear scoring, comments, and saved work help students improve quickly."
              points={[
                '10-point criteria for drills',
                'AI + teacher feedback',
                'Saved attempts for reflection',
              ]}
              cta={{ to: '/settings', label: 'View Preferences' }}
            />

            {/* Personalization */}
            <FeatureCard
              title="Personalization"
              blurb="Fine-tune how you practice, and track what matters to you."
              points={[
                'Difficulty, time limits, and writing options',
                'Prefer Gov/Opp — or randomize',
                'Profile & privacy controls',
              ]}
              cta={{ to: '/settings', label: 'Customize Settings' }}
            />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-6 py-12 md:py-16">
        <h2 className="text-3xl font-bold text-gray-900">How it works</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-8">
          <Step
            n="1"
            title="Pick a motion or module"
            text="Use AI motion generation in Training/AI Debater, or start a teacher-assigned lesson in your classroom."
          />
          <Step
            n="2"
            title="Practice with structure"
            text="Respond to focused prompts (PAEL, refutation targets, clash mapping, models, frames) and get instant feedback."
          />
          <Step
            n="3"
            title="Apply in live debate"
            text="Switch to the AI Debater to test your arguments under pressure — or bring the skills into tournament rounds."
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-t">
        <div className="max-w-6xl mx-auto px-6 py-12 md:py-16">
          <h2 className="text-3xl font-bold text-gray-900">Frequently asked questions</h2>
          <div className="grid md:grid-cols-2 gap-8 mt-8">
            <FAQ
              q="What debate formats is this useful for?"
              a="Our drills map cleanly to BP, WSDC, PF, and Policy fundamentals — especially argumentation, refutation, modeling, and framing. Teachers can tailor lessons to any format."
            />
            <FAQ
              q="How does AI feedback work?"
              a="We score with 10-point rubrics (e.g., analysis, impact/link, clarity). Feedback is concise, impersonal, and actionable. Teachers can override or add comments/grades."
            />
            <FAQ
              q="Can teachers manage classes?"
              a="Yes. Create classrooms with join codes, assign lessons, view completion, AI scores, and add teacher grades/feedback."
            />
            <FAQ
              q="Do students need accounts?"
              a="Yes — accounts ensure progress, attempts, and feedback are saved, and allow classroom assignments and grades."
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 py-14 md:py-20">
        <div className="rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-10 md:py-14 text-white">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-extrabold">Ready to level up?</h2>
              <p className="mt-3 text-blue-50">
                Jump into focused drills, or spar with the AI Debater. Teachers can create
                classrooms and start assigning lessons today.
              </p>
            </div>
            <div className="flex md:justify-end gap-3">
              <Link
                to="/training"
                className="inline-block bg-white text-blue-700 hover:bg-blue-50 font-semibold px-5 py-3 rounded-lg"
              >
                Train Now
              </Link>
              <Link
                to="/classrooms"
                className="inline-block bg-blue-800 hover:bg-blue-700 text-white font-semibold px-5 py-3 rounded-lg"
              >
                For Teachers
              </Link>
            </div>
          </div>
        </div>
        <p className="mt-6 text-sm text-gray-500">
          Questions? Reach us at <a href="mailto:debateacademy@derinedu.com" className="text-blue-600 hover:underline">debateacademy@derinedu.com</a>
        </p>
      </section>
    </div>
  );
}

/* ------------ Small presentational components ------------ */

function FeatureCard({ title, blurb, points = [], cta }) {
  return (
    <div className="bg-white border rounded-xl p-6 hover:shadow-sm transition">
      <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
      <p className="mt-2 text-gray-700">{blurb}</p>
      {points.length > 0 && (
        <ul className="mt-4 space-y-2 text-gray-700">
          {points.map((p, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-blue-600">•</span>
              <span>{p}</span>
            </li>
          ))}
        </ul>
      )}
      {cta && (
        <div className="mt-5">
          <Link
            to={cta.to}
            className="inline-block text-blue-700 hover:text-blue-600 font-medium"
          >
            {cta.label} →
          </Link>
        </div>
      )}
    </div>
  );
}

function Step({ n, title, text }) {
  return (
    <div className="bg-white border rounded-xl p-6">
      <div className="h-9 w-9 flex items-center justify-center rounded-full bg-blue-600 text-white font-bold">
        {n}
      </div>
      <h3 className="mt-4 text-lg font-semibold text-gray-900">{title}</h3>
      <p className="mt-2 text-gray-700">{text}</p>
    </div>
  );
}

function FAQ({ q, a }) {
  return (
    <div className="bg-gray-50 border rounded-xl p-6">
      <h4 className="font-semibold text-gray-900">{q}</h4>
      <p className="mt-2 text-gray-700">{a}</p>
    </div>
  );
}
